import { useLocalization } from '@lib/localization';
import { defaultCustomizationLocales, useGetConfigQuery, type CustomizationLocales } from '@store/api/rootEndpoints';
import { useMemo } from 'react';

/** Server locale set, plus the language this particular reader wants to see it in. */
export type ResolvedCustomizationLocales = CustomizationLocales & {
  /** The reader's interface language, chosen in Settings. */
  preferred: string;
};

/**
 * The languages user-authored customization content is written in, from server config.
 *
 * Server-wide rather than per team, so the same answer holds for a type you own and for
 * one shared with you by another team — which is the whole reason it is not a team
 * setting. Falls back to the built-in default while config is still loading, so a label
 * never resolves against an empty locale set.
 *
 * `preferred` rides along so that passing this object straight to `resolveLabel` shows a
 * reader their own language, with `primary` only as the fallback. Authoring is the other
 * way round: a label is *written* in `primary`, because the API requires it.
 */
export const useCustomizationLocales = (): ResolvedCustomizationLocales => {
  const { data: config } = useGetConfigQuery();
  const { currentLanguage } = useLocalization();
  const stored = config?.customizationLocales ?? defaultCustomizationLocales;

  // One object until the languages or the reader's language really change, so that a caller
  // can depend on it: the cache keeps the stored set's identity while a re-read brings nothing
  // new. The languages drawer started its draft again whenever this changed, and handed a new
  // object on every render it lost its edits to any re-render of its host. It goes by the
  // set's `primary` and `enabled` now, which the cache keeps the same way.
  return useMemo(() => ({ ...stored, preferred: currentLanguage }), [stored, currentLanguage]);
};
